import React, { useState, useEffect, useContext } from "react";
import { useFormContext } from "../createworkflow/FormContext";
import MenuContext from "../createworkflow/MenuContext";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { ArrowRight, PlusCircle, UploadCloud } from "lucide-react";

export default function Stage5() {
  const { formData, updateFormData } = useFormContext();
  const { setActiveTab } = useContext(MenuContext);

  // 🔹 Dummy Events for UI
  const dummyEvents = [
    { name: "Diwali Promotion", type: "Promotion", range: "Quarterly" },
    { name: "Holiday Discount", type: "Holiday", range: "Weekly" },
    { name: "Diwali Promotion", type: "Promotion", range: "Quarterly" },
  ];

  // const [events, setEvents] = useState(formData.stage5?.events || dummyEvents);
  const [events, setEvents] = useState(dummyEvents);
  const [eventName, setEventName] = useState("");
  const [eventType, setEventType] = useState("Promotion");
  const [customEventType, setCustomEventType] = useState("");

  // ✅ Month toggles state (0 = inactive, 1 = active)
  const [monthToggles, setMonthToggles] = useState({
    January: 0,
    February: 0,
    March: 0,
    April: 0,
    May: 0,
    June: 0,
    July: 0,
    August: 0,
    September: 0,
    October: 0,
    November: 0,
    December: 0,
  });

  useEffect(() => {
    updateFormData("stage5", { ...formData.stage5, events });
  }, [events]);

  // ✅ Toggle month handler
  const handleMonthToggle = (month) => {
    setMonthToggles((prev) => ({
      ...prev,
      [month]: prev[month] === 0 ? 1 : 0,
    }));
  };

  // ✅ Add Event with API call
  const handleAddEvent = async () => {
    if (!eventName) {
      alert("Please enter an event name");
      return;
    }

    if (eventType === "Custom" && !customEventType.trim()) {
      alert("Please enter a custom event type");
      return;
    }

    const finalEventType =
      eventType === "Custom" ? customEventType.trim() : eventType;

    const payload = {
      eventName,
      eventType: finalEventType,
      monthToggles,
    };

    // TODO: API call with payload, then update events
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      updateFormData("stage5", { ...formData.stage5, uploadedFile: file.name });
    }
  };

  const handleNext = () => setActiveTab("Stage6");
  const handleBack = () => setActiveTab("Stage4");

  // ✅ Split months into 2 groups of 6
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const firstHalfMonths = months.slice(0, 6);
  const secondHalfMonths = months.slice(6, 12);

  return (
    <ErrorBoundary>
      <div style={styles.wrapper}>
        <div style={styles.card}>
          {/* Progress Indicator */}
          <div className="flex items-center justify-start mb-8">
            {[1, 2, 3, 4, 5, 6, 7].map((step, index) => (
              <React.Fragment key={step}>
                <div
                  className={`flex items-center justify-center rounded-full ${
                    step === 5
                      ? "bg-[#032B4E] text-white"
                      : "bg-gray-200 text-gray-600"
                  }`}
                  style={{
                    width: "36px",
                    height: "36px",
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    border:
                      step === 5 ? "2px solid #032B4E" : "2px solid #d1d5db",
                  }}
                >
                  {step}
                </div>
                {index < 6 && (
                  <div
                    className={`h-0.5 ${
                      step < 5 ? "bg-[#032B4E]" : "bg-gray-300"
                    }`}
                    style={{ width: "40px" }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          <h2
            className="text-[30px] text-[#0F1116] text-left mb-8"
            style={{ fontFamily: "'Poppins', serif", fontWeight: 600 }}
          >
            Step 5: Event & Seasonality Layer
          </h2>

          {/* Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Add Event */}
            <div
              style={{ background: "#ffffff" }}
              className="bg-gradient-to-br from-[#FFFFFF] to-[#F8FAFF] border-2 border-[#475C7E] rounded-2xl shadow-[0_8px_20px_rgba(0,0,0,0.08)] p-8 transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)] hover:-translate-y-1.5"
            >
              <h3
                className="text-[20px] mb-6"
                style={{
                  fontFamily: "'Poppins', serif",
                  fontWeight: 600,
                  color: "#0F1116",
                }}
              >
                Add Event
              </h3>
              <div className="space-y-5">
                <div>
                  <label
                    className="text-[14px] font-medium text-gray-700"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Event Name
                  </label>
                  <input
                    type="text"
                    value={eventName}
                    onChange={(e) => setEventName(e.target.value)}
                    className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#032B4E] focus:border-[#032B4E] outline-none text-[14px] shadow-sm"
                    placeholder="Enter event name"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  />
                </div>

                <div>
                  <label
                    className="text-[14px] font-medium text-gray-700"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Type
                  </label>
                  <select
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#032B4E] outline-none text-[14px] shadow-sm"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    <option value="Promotion">Promotion</option>
                    <option value="Holiday">Holiday</option>
                    <option value="Campaign">Campaign</option>
                    <option value="Custom">Custom</option>
                  </select>
                  {eventType === "Custom" && (
                    <div className="mt-4">
                      <label
                        className="text-[14px] font-medium text-gray-700"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      >
                        Custom Type
                      </label>
                      <input
                        type="text"
                        value={customEventType}
                        onChange={(e) =>
                          setCustomEventType(e.target.value)
                        }
                        placeholder="Enter custom event type"
                        className="w-full mt-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#032B4E] outline-none text-[14px] shadow-sm"
                        style={{ fontFamily: "Inter, sans-serif" }}
                      />
                    </div>
                  )}
                </div>

                {/* Month Toggle Table - 2 Columns */}
                <div>
                  <label
                    className="text-[14px] font-medium text-gray-700 mb-3 block"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    Select Active Months
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    {/* First Column (Jan-Jun) */}
                    <div className="bg-white border-2 border-[#475C7E] rounded-xl shadow-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-[#032B4E] text-white">
                          <tr>
                            <th
                              className="py-2 px-3 text-left text-[12px]"
                              style={{
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 600,
                              }}
                            >
                              Month
                            </th>
                            <th
                              className="py-2 px-3 text-center text-[12px]"
                              style={{
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 600,
                              }}
                            >
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {firstHalfMonths.map((month, idx) => (
                            <tr
                              key={month}
                              className={`border-b border-gray-200 hover:bg-[#f5fbff] transition-all duration-200 ${
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                            >
                              <td
                                className="py-2 px-3 text-[12px] text-gray-800"
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 500,
                                }}
                              >
                                {month}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <button
                                  onClick={() => handleMonthToggle(month)}
                                  className={`w-12 h-6 rounded-full transition-all duration-300 shadow-md ${
                                    monthToggles[month] === 1
                                      ? "bg-[#032B4E]"
                                      : "bg-gray-300"
                                  }`}
                                  style={{ position: "relative" }}
                                >
                                  <span
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${
                                      monthToggles[month] === 1
                                        ? "translate-x-6"
                                        : "translate-x-0"
                                    }`}
                                  ></span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Second Column (Jul-Dec) */}
                    <div className="bg-white border-2 border-[#475C7E] rounded-xl shadow-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-[#032B4E] text-white">
                          <tr>
                            <th
                              className="py-2 px-3 text-left text-[12px]"
                              style={{
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 600,
                              }}
                            >
                              Month
                            </th>
                            <th
                              className="py-2 px-3 text-center text-[12px]"
                              style={{
                                fontFamily: "'Inter', sans-serif",
                                fontWeight: 600,
                              }}
                            >
                              Active
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {secondHalfMonths.map((month, idx) => (
                            <tr
                              key={month}
                              className={`border-b border-gray-200 hover:bg-[#f5fbff] transition-all duration-200 ${
                                idx % 2 === 0 ? "bg-white" : "bg-gray-50"
                              }`}
                            >
                              <td
                                className="py-2 px-3 text-[12px] text-gray-800"
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontWeight: 500,
                                }}
                              >
                                {month}
                              </td>
                              <td className="py-2 px-3 text-center">
                                <button
                                  onClick={() => handleMonthToggle(month)}
                                  className={`w-12 h-6 rounded-full transition-all duration-300 shadow-md ${
                                    monthToggles[month] === 1
                                      ? "bg-[#032B4E]"
                                      : "bg-gray-300"
                                  }`}
                                  style={{ position: "relative" }}
                                >
                                  <span
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-300 ${
                                      monthToggles[month] === 1
                                        ? "translate-x-6"
                                        : "translate-x-0"
                                    }`}
                                  ></span>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Upload Event button (optional) */}
              </div>

              <p
                className="text-[14px] text-gray-500 mt-5 italic"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Auto-saving as draft...
              </p>
            </div>

            {/* Events List */}
            <div
              style={{ background: "#ffffff" }}
              className="bg-gradient-to-br from-[#FFFFFF] to-[#F8FAFF] border-2 border-[#475C7E] rounded-2xl shadow-[0_8px_20px_rgba(0,0,0,0.08)] p-8 transition-all duration-300 hover:shadow-[0_12px_30px_rgba(0,0,0,0.12)] hover:-translate-y-1.5 flex flex-col"
            >
              <div className="flex items-center justify-between mb-6">
                <h3
                  className="text-[20px]"
                  style={{
                    fontFamily: "'Poppins', serif",
                    fontWeight: 600,
                    color: "#0F1116",
                  }}
                >
                  Events ({events.length})
                </h3>
                <span
                  className="text-[14px] bg-blue-100 px-3 py-1 rounded-md shadow-sm"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    color: "#0F1116",
                  }}
                >
                  Draft Saved
                </span>
              </div>

              {/* Middle area: table (auto height) + flexible upload box */}
              <div className="flex-1 flex flex-col gap-5">
                {/* Table: just wraps rows, no flex-1 */}
                <div className="bg-white border-2 border-[#475C7E] rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-[0_16px_40px_rgba(0,128,0,0.25)] hover:scale-[1.01]">
                  <table className="w-full text-left">
                    <thead className="bg-[#032B4E] text-white sticky top-0 z-10">
                      <tr>
                        <th
                          className="py-3 px-4 text-[14px]"
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Event Name
                        </th>
                        <th
                          className="py-3 px-4 text-[14px]"
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Type
                        </th>
                        <th
                          className="py-3 px-4 text-[14px]"
                          style={{
                            fontFamily: "'Inter', sans-serif",
                            fontWeight: 600,
                          }}
                        >
                          Range
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((event, idx) => (
                        <tr
                          key={idx}
                          className="border-b border-[#d8f0e4] hover:bg-[#f5fbff] transition-all duration-200"
                        >
                          <td
                            className="py-4 px-6 font-semibold text-[14px]"
                            style={{
                              fontFamily: "Inter, sans-serif",
                              color: "#0F1116",
                            }}
                          >
                            {event.name}
                          </td>
                          <td
                            className="py-4 px-6 text-gray-700 text-[14px]"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            {event.type}
                          </td>

                          <td
                            className="py-4 px-6 text-[14px]"
                            style={{ fontFamily: "Inter, sans-serif" }}
                          >
                            <span
                              className={`px-3 py-1 text-sm font-medium rounded-full ${
                                event.range === "Weekly"
                                  ? "bg-[#1F4280] bg-opacity-20 text-[#1F4280]"
                                  : event.range === "Quarterly"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {event.range}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Upload box: fills remaining space, grows/shrinks with table */}
                <div className="flex-1 border border-dashed border-[#1F4280] rounded-xl p-8 text-center bg-[#F9FBFF] hover:border-[#163464] hover:shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-center">
                  <UploadCloud className="w-12 h-12 text-[#1F4280] mx-auto mb-3 animate-pulse" />
                  <p
                    className="mb-1 text-[14px]"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 600,
                      color: "#0F1116",
                    }}
                  >
                    Drag and drop or Upload
                  </p>
                  <p
                    className="text-[14px] text-gray-500 mb-3"
                    style={{
                      fontFamily: "'Inter', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    CSV to add events in bulk with seasonality values included
                  </p>
                  <label className="cursor-pointer">
                    <span
                      className="text-[#b8842f] hover:underline text-[14px]"
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      Download CSV Template
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleFileUpload}
                      accept=".csv, .xls, .xlsx"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Navigation */}
          <div className="mt-10 flex justify-between items-center">
            <button
              onClick={handleBack}
              className="px-8 py-2.5 bg-[#b8842f] hover:bg-[#a67d2e] text-white rounded-lg transition-all shadow-lg hover:shadow-xl"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              ← Back
            </button>
            <button
              onClick={handleNext}
              className="px-8 py-2.5 rounded-lg bg-[#b8842f] text-white hover:bg-[#a67d2e] shadow-lg hover:shadow-xl flex items-center gap-2 transition-all"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}

const styles = {
  wrapper: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:"linear-gradient(135deg, #021a30 0%, #032B4E 30%, #032B4E 70%, #021a30 100%)",
    padding: "60px 0",
    boxSizing: "border-box",
  },
  card: {
    width: "95%",
    maxWidth: 1500,
    background: "#fbfcf7ff",
    padding: "40px 40px 60px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
    border: "1px solid #D9E1EC",
    overflow: "hidden",
  },
};
